import {
  SNAPSHOT_MAX_ELEMENTS,
  SNAPSHOT_MAX_ELEMENT_TEXT_CHARS,
  SNAPSHOT_MAX_HREF_CHARS,
  SNAPSHOT_MAX_TEXT_CHARS,
  type PageSnapshot,
  type PageSnapshotElement,
} from "@ai-guidance/shared";
import { collapseProductCards } from "./commerce/snapshotProducts";
/**
 * Snapshot al paginii vii, capturat la fiecare întrebare: ce se vede pe ecran
 * + elementele pe care agentul le poate arăta. Înlocuiește ghicitul din config
 * - agentul răspunde despre pagina care e ACUM în fața vizitatorului.
 *
 * Regula care nu se negociază: NU capturăm niciodată `value` din inputuri.
 * Nici parole, nici câmpuri completate, nici măcar „inofensive". Luăm doar text
 * randat (`innerText`, care prin definiție nu include valorile din inputuri) și
 * etichete de elemente (aria-label / text / placeholder - placeholder-ul e
 * scris de dezvoltatorul sitului, nu de vizitator). Nu e o preferință de
 * design: privacy §2.2 și DPA Annex I promit exact asta clienților și
 * vizitatorilor lor (registru → G12). `snapshot.test.ts` o apără explicit.
 *
 * Harta `idx → Element` trăiește doar aici, în memorie: e reconstruită la
 * fiecare captură și nu pleacă niciodată la server (serverul vede doar numerele
 * și etichetele). De-aia un `idx` primit înapoi de la model se rezolvă instant,
 * fără querySelector - vezi `resolve.ts`.
 */

/**
 * Elementele noastre - panoul de builder, badge-ul - nu sunt parte din situl clientului.
 *
 * Widgetul de chat NU mai depinde de lista asta: de când trăiește într-un shadow root
 * (`widget.ts`), conținutul lui nu e atins nici de `cloneNode`, nici de
 * `querySelectorAll` din light DOM. Înainte, faptul că mesajele noastre nu ajungeau la
 * model ținea de un singur string - o redenumire a clasei `.aig-root` le-ar fi trimis
 * în tăcere, cu testele verzi. Acum garanția e structurală; `.aig-root` rămâne aici doar
 * ca centură dublă pentru containerul gol rămas în light DOM.
 */
const OWN_ELEMENTS_SELECTOR = "[data-ai-guidance],[data-aig-builder],.aig-root";

/**
 * Zone excluse din snapshot LA CEREREA proprietarului sitului: subtree-ul
 * potrivit nu se citește și nu pleacă la LLM - nici în `text`, nici în
 * `elements`. Două căi, ambele active mereu:
 *  - atributul static `data-aig-snapshot-exclude`, pus direct în HTML-ul
 *    sitului (zero configurare);
 *  - selectoarele CSS din settings (apps.excluded_selectors → /config →
 *    `setSnapshotExclusions`), pentru situri unde HTML-ul nu se poate atinge.
 * Serverul nu validează gramatica CSS (n-are DOM) - aici selectoarele invalide
 * se aruncă defensiv la setare, ca un singur selector stricat să nu anuleze
 * întreaga listă la fiecare captură. Registru legal: G20.
 */
const SNAPSHOT_EXCLUDE_ATTR_SELECTOR = "[data-aig-snapshot-exclude]";

let customExclusions: string[] = [];

export function setSnapshotExclusions(selectors: string[]): void {
  const probe = document.createDocumentFragment();
  customExclusions = selectors.filter((s) => {
    try {
      probe.querySelector(s);
      return true;
    } catch {
      console.warn(`[ai-guidance] ignoring invalid excluded selector: ${s}`);
      return false;
    }
  });
}

function exclusionSelector(): string {
  return [SNAPSHOT_EXCLUDE_ATTR_SELECTOR, ...customExclusions].join(",");
}

/**
 * Pe un magazin, textul cardurilor de produs se comprimă la o linie per produs
 * (`commerce/snapshotProducts.ts`). Semnalul e pornit de `sdk.ts` în același loc
 * în care se naște `CommerceRuntime` - adică exact când appul are `commerce` în
 * /config. Un flag de modul, ca `customExclusions`: captura nu primește obiecte
 * injectate de nicăieri, iar pe orice sit care nu e magazin comportamentul
 * rămâne bit cu bit cel de dinainte.
 */
let commerceActive = false;

export function setCommerceSnapshot(active: boolean): void {
  commerceActive = active;
}

/**
 * Exportat pentru actionTrail.ts: excluderea proprietarului acoperă TOT ce
 * pleacă la LLM, nu doar snapshotul - un click într-o zonă exclusă nu se
 * înregistrează, altfel trail-ul ar fi o ușă din spate pe lângă excludere.
 */
export function isExcludedElement(el: Element): boolean {
  return el.closest(exclusionSelector()) !== null;
}

/**
 * Câmpuri al căror TEXT e, de fapt, o valoare: conținutul unui `<textarea>` E
 * value-ul lui (un mesaj salvat, o adresă, un tichet de suport), iar textul
 * randat al unui `<select>` e opțiunea aleasă de om. Amândouă ajung în
 * `innerText`/`textContent`, deci fără golirea lor explicită promisiunea „nu
 * capturăm valori din formulare" ar fi falsă - `value` nu e singurul drum prin
 * care o valoare iese din pagină. Golite peste tot: și în textul paginii, și în
 * etichetele elementelor.
 */
const VALUE_BEARING_SELECTOR = "textarea, select";

function isFormField(el: Element): boolean {
  return el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT";
}

/**
 * Textul vizibil al unui element, cu conținutul câmpurilor de formular scos.
 *
 * `innerText`, nu `textContent`, din același motiv pentru care textul paginii
 * îl folosește (`visibleText`): e ce vede omul. `textContent` întoarce ȘI
 * textul descendenților nerandați și lipește cuvintele peste granițele de
 * element. Măsurat pe demo (Atlantic Solar), o listă de FAQ cu `<details>`
 * închise ieșea „Solar basicsWhat does self-consumption mean?Self-c…" - cu
 * răspunsul ascuns înăuntru, deși canalul de text îl excludea corect, și cu
 * „basicsWhat" lipit. `innerText` dă „Solar basics What does self-consumption
 * mean? Do I…". Costul e același (0,2 ms pentru 70 de elemente): layoutul e
 * deja calculat, iar citirile se grupează.
 *
 * `innerText` lipsește în jsdom (și pe noduri detașate), de unde căderea pe
 * `textContent` - la fel ca în `visibleText`.
 */
function safeTextOf(el: Element): string {
  const collapse = (text: string): string => text.replace(/\s+/g, " ").trim();
  // clona costă: o plătim doar când chiar există un câmp înăuntru (butoane,
  // linkuri, heading-uri - cazul normal - n-au)
  if (!el.querySelector(VALUE_BEARING_SELECTOR)) {
    return collapse((el as HTMLElement).innerText ?? el.textContent ?? "");
  }
  // Cu un câmp înăuntru rămânem pe clonă + `textContent`: clona e detașată,
  // deci n-are layout și `innerText` n-ar întoarce nimic util, iar a o atașa
  // pentru fiecare element ar fi scump. Golirea câmpurilor rămâne prioritară -
  // e o promisiune publică (privacy §2.2), eticheta e doar comoditate.
  const clone = el.cloneNode(true) as Element;
  clone.querySelectorAll(VALUE_BEARING_SELECTOR).forEach((field) => (field.textContent = ""));
  return collapse(clone.textContent ?? "");
}

/**
 * Eticheta cu care agentul se referă la un element. Ca `labelFor` din
 * builder/selector.ts, cu o diferență care contează: pentru câmpurile de
 * formular nu atingem NICIODATĂ textul lor - pentru un textarea ăla e conținutul
 * tastat, pentru un select e opțiunea aleasă. Rămâne ce a scris dezvoltatorul
 * sitului: aria-label, `<label for>`-ul asociat, placeholder, title, name.
 */
function snapshotLabelFor(el: Element): string {
  const aria = el.getAttribute("aria-label");
  if (aria) return aria;
  if (isFormField(el)) {
    const labelled = (el as HTMLInputElement).labels?.[0]?.textContent?.replace(/\s+/g, " ").trim();
    return (
      labelled ||
      el.getAttribute("placeholder") ||
      el.getAttribute("title") ||
      el.getAttribute("name") ||
      el.tagName.toLowerCase()
    );
  }
  return (
    safeTextOf(el) ||
    el.getAttribute("placeholder") ||
    el.getAttribute("alt") ||
    el.getAttribute("title") ||
    el.tagName.toLowerCase()
  );
}

/**
 * Starea de dezvăluire a unui element, când situl o declară el însuși.
 *
 * Ăsta e vocabularul pe care browserul îl are deja pentru „în spatele meu e
 * conținut, acum ascuns" - `aria-expanded`, `role=tab` + `aria-selected`,
 * `<details>`. Nu-l inferăm și nu-l ghicim: fără atributele astea nu spunem
 * nimic, fiindcă o muchie greșită („butonul X deschide panoul Y", când de fapt
 * nu) produce o ghidare încrezătoare și falsă - mai rea decât una lipsă.
 * Euristica „panoul e fratele următor" a fost testată pe Allbirds și pică
 * deplasat cu un rând, de-aia nu există aici.
 *
 * Acoperirea e inegală și merită știut: taburile de produs WooCommerce o au
 * completă (`aria-controls` + `aria-selected` pe toate trei), Brooklinen 82%,
 * iar acordeoanele de produs Allbirds au `aria-expanded` fără `aria-controls` -
 * adică starea, dar nu ținta. De-aia raportăm doar starea: e partea pe care o
 * putem afirma peste tot unde există.
 */
function disclosureStateOf(el: Element): PageSnapshotElement["state"] {
  const expanded = el.getAttribute("aria-expanded");
  if (expanded === "false") return "collapsed";
  if (expanded === "true") return "expanded";
  if (el.getAttribute("role") === "tab") {
    return el.getAttribute("aria-selected") === "true" ? "selected" : "not selected";
  }
  if (el.tagName === "SUMMARY") {
    return (el.parentElement as HTMLDetailsElement | null)?.open ? "expanded" : "collapsed";
  }
  return undefined;
}

/**
 * Ținte posibile: interactivele (aceeași listă ca la scanare, `builder/elements.ts`)
 * plus heading-uri și secțiuni - „găsești în secțiunea asta" e o cerință de
 * produs, iar un `scrollTo` pe un h2 e răspunsul; un buton nu e.
 */
const TARGETABLE_SELECTOR =
  "button, a, input, select, textarea, [role='button'], [onclick], [data-ai-id], h1, h2, h3, section[id], [role='region'], summary, [role='tab'], [aria-expanded]";

/** Containerele de conținut real, în ordinea preferinței, când pagina întreagă e prea mare. */
const MAIN_SELECTOR = "main, [role='main'], article";

function isOwnElement(el: Element): boolean {
  return el.closest(OWN_ELEMENTS_SELECTOR) !== null;
}

/**
 * Detectat o singură dată per captură, nu per element: jsdom (folosit de teste)
 * n-are motor de layout - `getClientRects()` întoarce mereu listă goală pentru
 * ORICE element, iar `checkVisibility` nici nu există acolo. Dacă am filtra
 * fără să verificăm asta, testele ar pierde toate elementele și codul din
 * producție s-ar comporta identic pe un site real fără layout încă flush-uit.
 * Regula (aceeași ca `visibleExtent` din guide.ts): când nu putem ști dacă un
 * element e vizibil, nu tăiem - o necunoaștere nu se prezintă drept constatare.
 */
function hasLayoutEngine(): boolean {
  return document.body.getClientRects().length > 0;
}

/**
 * `getComputedStyle(el).display === "none"` e greșit: un element al cărui
 * STRĂMOȘ e display:none are propriul display computed normal (măsurat:
 * `inline-block`) - cazul dominant fiind exact un container de meniu ascuns
 * cu copii afișați normal „pe hârtie". `getClientRects().length === 0` prinde
 * display:none oriunde-n lanțul de strămoși, cu suport universal.
 * `checkVisibility()` e mai complet unde există (Safari 17.4+/Firefox 125+),
 * dar DOAR cu `checkVisibilityCSS: true`. Măsurat în Chrome: fără opțiunea
 * asta întoarce `true` pentru `visibility:hidden`, adică e strict echivalent
 * cu `getClientRects()` și n-ar aduce nimic. Cu ea, prinde și
 * `visibility:hidden` - util fiindcă un asemenea element ARE dreptunghiuri,
 * deci spotlight-ul i-ar desena un inel în jurul unui gol.
 *
 * Pe browserele fără `checkVisibility` rămân câteva elemente în plus față de
 * cele noi. E aceeași regulă ca peste tot aici: unde nu putem ști, nu tăiem.
 *
 * Nu verificăm `opacity` (fade-in-uri și controale custom stilizate peste
 * inputuri native, ambele legitime) și nu verificăm poziția față de viewport
 * - sub linia de plutire e vizibil, doar nu pe ecran ACUM, iar
 * `getClientRects()` întoarce dreptunghiuri și pentru elemente off-screen, deci
 * asta rămâne țintă (comportamentul dorit iese singur, fără cod în plus).
 */
function isVisible(el: Element): boolean {
  if (typeof el.checkVisibility === "function") return el.checkVisibility({ checkVisibilityCSS: true });
  return el.getClientRects().length > 0;
}

/**
 * Acoperit de altceva - un modal, un banner de cookie-uri, orice overlay.
 * Elementul e „vizibil" după orice criteriu CSS, dar vizitatorul nu poate
 * ajunge la el, iar spotlight-ul i-ar desena inelul SUB overlay.
 *
 * Măsurat pe demo2 (Orbit CRM) cu un modal deschis: 31 din 45 de elemente din
 * snapshot erau fundalul aplicației, oferite modelului ca ținte. `aria-hidden`
 * nu ajută - pe situl ăla e pus pe 21 de avataruri decorative și deloc pe
 * fundal, exact fals-pozitivul care l-ar face inutilizabil. `inert` nu e
 * folosit. Ce funcționează e testul de acoperire, care nu depinde de cât de
 * disciplinat e situl clientului: prinde la fel modale, bannere și heedere
 * lipicioase.
 *
 * Două locuri unde refuzăm să tragem concluzii:
 * - viewport necitibil → nu tăiem;
 * - centrul elementului în afara ecranului → hit-testingul nu se aplică acolo,
 *   iar sub linia de plutire înseamnă vizibil, doar nu pe ecran ACUM. Fără
 *   garda asta am fi eliminat tot ce e mai jos de fold (măsurat: 14 din 45).
 *
 * Cost măsurat: 1,3 ms pentru 45 de elemente, deci ~6 ms la plafonul de 200.
 */
function isCovered(el: Element): boolean {
  if (typeof document.elementFromPoint !== "function") return false;
  const vw = window.innerWidth || document.documentElement.clientWidth;
  const vh = window.innerHeight || document.documentElement.clientHeight;
  if (vw <= 0 || vh <= 0) return false;

  const rect = el.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  // Trei puncte pe verticală, nu doar centrul. Un singur punct e prea fragil:
  // un header `sticky` care acoperă doar partea de sus a unui element l-ar
  // scoate cu totul, deși restul lui e liber și accesibil - iar headerele
  // lipicioase sunt regula pe siturile de prezentare (demo, Atlantic Solar, are
  // unul). Acoperit înseamnă acoperit în TOATE punctele testate: un overlay de
  // modal le ia pe toate, un header doar pe primul.
  const points = [0.25, 0.5, 0.75].map((f) => rect.top + rect.height * f);
  let tested = 0;
  for (const cy of points) {
    if (cx < 0 || cy < 0 || cx >= vw || cy >= vh) continue;
    tested++;
    if (!isPointCovered(el, cx, cy)) return false;
  }
  // Niciun punct testabil (element complet în afara ecranului): nu tăiem.
  return tested > 0;
}

/** Un punct anume e acoperit de altceva decât elementul însuși? */
function isPointCovered(el: Element, x: number, y: number): boolean {
  const top = document.elementFromPoint(x, y);
  if (!top) return false;
  // Propriul nostru widget nu contează drept acoperire. Panoul de chat e `fixed`
  // în colț și opac la hit-testing, deci ar ascunde de model exact elementele
  // sitului aflate în spatele lui - am ciunti pagina clientului din cauza UI-ului
  // nostru, pe care vizitatorul îl poate oricum muta sau închide. (Inelul
  // spotlight-ului nu pune problema: e `pointer-events: none`, deci transparent
  // la elementFromPoint.)
  if (isOwnElement(top)) return false;
  // `contains` în ambele sensuri: elementul din vârf poate fi un copil al țintei
  // (o iconiță din buton) sau un părinte al ei (o etichetă care o înfășoară).
  return !el.contains(top) && !top.contains(el);
}

/**
 * Atribut de marcaj pus pe DOM-ul VIU o clipă, ca să putem șterge din clonă
 * nodurile pe care doar pagina vie știe să le judece (clipping-ul cere layout,
 * iar clona n-are layoutul originalului). Marcăm → clonăm → demarcăm, totul
 * sincron: un MutationObserver al sitului își vede callback-ul abia în
 * microtask, cu DOM-ul deja pus la loc.
 */
const CLIPPED_MARK = "data-aig-clipped";

/** Valorile de `overflow` care chiar taie conținutul dintr-un container de dimensiune 0. */
const CLIPPING_OVERFLOW = /hidden|clip|scroll|auto/;

/**
 * Conținut ascuns prin **clipping** - containerul are o dimensiune 0 și
 * `overflow` ne-vizibil, deci textul dinăuntru e randat, dar tăiat de pe ecran.
 *
 * Fără asta, promisiunea din privacy §2.2 („the text content **visible** on the
 * page") era falsă, și fals exact pe jumătate: `innerText` exclude
 * `display:none` și `visibility:hidden`, dar NU conținutul clipuit - mecanismul
 * preferat al temelor Shopify pentru acordeoane. Filtrul de elemente era corect
 * (`checkVisibility` întoarce false, deci panoul nu ajungea în listă); doar
 * textul scăpa. Măsurat 19 iul 2026: pe o pagină de produs Brooklinen se
 * scurgeau 3 panouri închise - „New & Improved", „Care" și **„Returns"** - iar
 * pe Allbirds instrucțiunile de spălare și compoziția materialelor. Modelul
 * primea deci răspunsul, dar nu și butonul care-l dezvăluie: răspundea „da, sunt
 * lavabile în mașină" unui om care se uită la un acordeon închis.
 *
 * De ce NU verificăm și `aria-expanded`/`aria-controls`: panourile ascunse
 * structural (`display:none`, cazul WooCommerce) sunt deja excluse de
 * `innerText`, deci regula ar fi redundantă - măsurat, n-a prins nimic în plus
 * și a triplat costul. Clipping-ul e singura gaură reală.
 *
 * Ordinea verificărilor e o decizie de cost: geometria întâi (iese imediat
 * pentru majoritatea nodurilor), `getComputedStyle` doar pe supraviețuitori -
 * 13,9 ms pe 7.153 de elemente, față de 50 ms în varianta naivă.
 */
function clippedContainers(): Element[] {
  const clipped: Element[] = [];
  for (const el of document.body.querySelectorAll("*")) {
    // notă: `clientWidth` e 0 și pentru elementele inline normale, deci trecerea
    // prin filtrul ăsta nu e o constatare - decide `overflow` de mai jos
    if (el.clientHeight !== 0 && el.clientWidth !== 0) continue;
    if (!(el.textContent ?? "").trim()) continue;
    // potrivire POZITIVĂ pe valorile care chiar taie, nu „orice nu e visible":
    // negația e falsă exact acolo unde nu putem ști - jsdom întoarce `""` pentru
    // `overflow` nesetat, iar regula negativă marca atunci ÎNTREAGA pagină drept
    // ascunsă (toate elementele au clientHeight 0 fără motor de layout).
    if (!CLIPPING_OVERFLOW.test(getComputedStyle(el).overflow)) continue;
    clipped.push(el);
  }
  return clipped;
}

/**
 * `innerText` ≈ ce vede omul: respectă display:none/visibility și nu întoarce
 * niciodată valorile din inputuri. Widgetul nostru e scos întâi din clonă -
 * altfel agentul ar citi propriile lui mesaje ca și cum ar fi conținut al paginii.
 * Conținutul clipuit se scoate și el (vezi `clippedContainers`).
 */
export function visibleText(root: HTMLElement, exclude: Element[] = [], collapseProducts = false): string {
  // fără motor de layout nu putem judeca clipping-ul; aceeași regulă ca peste
  // tot aici - unde nu putem ști, nu tăiem. `exclude` (opțional) permite
  // apelantului să scoată subarbori întregi pe același drum cu clipping-ul -
  // folosit de scanare (builder/dom.ts) pentru cromul de consimțământ; snapshot-ul
  // de runtime NU exclude nimic în plus (vizitatorul chiar vede bannerul).
  const clipped = hasLayoutEngine() ? clippedContainers() : [];
  const marked = [...clipped, ...exclude];
  marked.forEach((el) => el.setAttribute(CLIPPED_MARK, ""));
  const clone = root.cloneNode(true) as HTMLElement;
  marked.forEach((el) => el.removeAttribute(CLIPPED_MARK));
  clone.querySelectorAll(`[${CLIPPED_MARK}]`).forEach((el) => el.remove());
  clone.querySelectorAll(OWN_ELEMENTS_SELECTOR).forEach((el) => el.remove());
  clone.querySelectorAll(exclusionSelector()).forEach((el) => el.remove());
  clone.querySelectorAll(VALUE_BEARING_SELECTOR).forEach((field) => (field.textContent = ""));
  // innerText cere layout, iar clona detașată n-are: o atașăm invizibil, o citim, o scoatem.
  // (jsdom nu implementează innerText - de-aia fallback-ul pe textContent.)
  clone.style.position = "absolute";
  clone.style.left = "-99999px";
  clone.setAttribute("data-ai-guidance", "snapshot-scratch");
  document.body.appendChild(clone);
  try {
    // După atașare: `safeTextOf` citește `innerText`, iar o clonă detașată n-are
    // layout. După curățări: un card exclus de proprietar nu mai există aici,
    // deci nu poate ajunge nici în lista comprimată.
    if (collapseProducts) collapseProductCards(clone, safeTextOf);
    return (clone.innerText ?? clone.textContent ?? "").replace(/\n{3,}/g, "\n\n").trim();
  } finally {
    clone.remove();
  }
}

/** Taie la ultima graniță de paragraf care încape - nu în mijlocul unui cuvânt. */
function truncateAtParagraph(text: string, max: number): string {
  if (text.length <= max) return text;
  const marker = "\n\n[…truncated]";
  const budget = max - marker.length;
  const head = text.slice(0, budget);
  const lastBreak = head.lastIndexOf("\n\n");
  // fără nicio graniță de paragraf în buget (pagină cu un singur bloc lung),
  // cădem pe ultimul spațiu - tot mai bine decât mid-cuvânt
  const cut = lastBreak > budget * 0.5 ? lastBreak : head.lastIndexOf(" ");
  return `${head.slice(0, cut > 0 ? cut : budget).trimEnd()}${marker}`;
}

/* --- Blocul „site contact" (regresia 30 oct 2026, C10c) -------------------------
   Când textul paginii nu încape și cădem pe `main`/`article` (sau pe tăietură),
   header-ul și footer-ul pleacă odată cu zgomotul - și cu ele telefonul, emailul
   și adresa, pe care multe situri le arată DOAR acolo. Pe Atlantica brieful a
   ajuns să afirme „telefonul/emailul/adresa nu sunt pe sit" deși erau în bara de
   sus și în footer pe fiecare pagină. Păstrăm doar liniile care ARATĂ a contact,
   într-un bloc mic și mărginit - nu header-ul întreg. */

/** Plafonul liniilor de contact adăugate (fără eticheta blocului). */
const CONTACT_MAX_CHARS = 400;
/** Rezerva scoasă din textul principal ca blocul să încapă sub plafonul de pe fir. */
const CONTACT_RESERVE = CONTACT_MAX_CHARS + 40;
const CONTACT_LABEL = "\n\n[site contact]\n";
const CONTACT_LINE_MAX = 200;
const CONTACT_MAX_ROOTS = 8;
/** Zone cu informații de contact: header/footer + barele de sus „de tip topbar". */
const CONTACT_ROOT_SELECTOR =
  "header, footer, [role='contentinfo'], [role='banner'], [class*='top-bar' i], [class*='topbar' i], [class*='top_bar' i], [id*='top-bar' i], [id*='topbar' i]";
/** Meniurile de cont arată datele VIZITATORULUI (email, telefon) - nu sunt ale sitului. */
const CONTACT_ACCOUNT_HINT = /account|user|profile|member|login|signin|sign-in/i;
const CONTACT_SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "SELECT", "INPUT", "TEMPLATE", "SVG", "IFRAME"]);
const CONTACT_BLOCK_TAGS = new Set([
  "ADDRESS", "ARTICLE", "ASIDE", "BR", "DD", "DIV", "DL", "DT", "FOOTER", "H1", "H2", "H3", "H4", "H5", "H6",
  "HEADER", "LI", "NAV", "OL", "P", "SECTION", "TABLE", "TD", "TH", "TR", "UL",
]);

const EMAIL_RE = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/;
const PHONE_CANDIDATE_RE = /\+?\d[\d\s().-]{6,}\d/g;
const DATE_LIKE_RE = /\d{4}[-./]\d{1,2}[-./]\d{1,2}|\d{1,2}[-./]\d{1,2}[-./]\d{2,4}/;
const YEAR_RANGE_RE = /^(?:19|20)\d{2}\s*[-–]\s*(?:19|20)\d{2}$/;
const STREET_WORD_RE = new RegExp(
  "(?:^|[\\s,(])(?:street|st\\.|avenue|ave\\.?|road|rd\\.|lane|boulevard|blvd\\.?|calle|c\\/|avda\\.?|avenida|paseo|plaza|carrer|strada|str\\.|bd\\.|bulevardul|șoseaua|soseaua|rue|straße|strasse|platz|via|viale|piazza|rua|travessa)(?=[\\s.,)]|$)",
  "i",
);
const POSTAL_CITY_RE = /(?:^|\s)\d{5,6}\s+\p{Lu}\p{L}/u;
const HOURS_WORD_RE =
  /\b(?:hours|opening|open|horario|horarios|abierto|orar|program|öffnungszeiten|geöffnet|horaires|ouvert|orario|aperto|horário|aberto|mon|monday|lun|lunes|fri|friday|vie|viernes|luni|vineri)\b/i;
const TIME_RE = /\b\d{1,2}[:.h]\d{2}\b|\b\d{1,2}\s?(?:am|pm)\b/i;
const TIME_RANGE_RE = /\b\d{1,2}[:.h]\d{2}\s*(?:-|–|—|to|a|à|bis|às)\s*\d{1,2}[:.h]\d{2}\b/i;

function looksLikePhone(line: string): boolean {
  for (const match of line.match(PHONE_CANDIDATE_RE) ?? []) {
    const digits = match.replace(/\D/g, "").length;
    if (digits < 7 || digits > 15) continue;
    if (YEAR_RANGE_RE.test(match.trim()) || DATE_LIKE_RE.test(match)) continue;
    return true;
  }
  return false;
}

/** Linia „arată a contact": telefon, email, adresă stradală sau program. */
function looksLikeContact(line: string): boolean {
  if (EMAIL_RE.test(line) || looksLikePhone(line)) return true;
  if (/\d/.test(line) && (STREET_WORD_RE.test(line) || POSTAL_CITY_RE.test(line))) return true;
  if (TIME_RANGE_RE.test(line)) return true;
  return HOURS_WORD_RE.test(line) && TIME_RE.test(line);
}

/** `tel:`/`mailto:` fără schemă și fără query - linkurile cu doar o iconiță n-au text. */
function linkPayload(el: Element): string {
  const href = el.getAttribute("href") ?? "";
  const m = /^(?:tel|mailto):([^?#]+)/i.exec(href);
  if (!m) return "";
  try {
    return decodeURIComponent(m[1]).trim();
  } catch {
    return m[1].trim();
  }
}

/**
 * Liniile de text ale unei zone, construite din DOM-ul viu: granița de linie e
 * un element de bloc, deci rezultatul e același în browser și în jsdom (unde
 * `innerText` lipsește). Sar: zonele excluse de proprietar, elementele
 * widgetului, câmpurile de formular (nicio valoare nu iese de aici), conținutul
 * ascuns și meniurile de cont.
 */
function collectContactLines(root: Element, out: string[]): void {
  const layout = hasLayoutEngine();
  const exclusion = exclusionSelector();
  let current = "";
  const flush = (): void => {
    const text = current.replace(/\s+/g, " ").trim();
    if (text) out.push(text);
    current = "";
  };
  const walk = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      current += node.textContent ?? "";
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    if (CONTACT_SKIP_TAGS.has(el.tagName.toUpperCase())) return;
    if (el.hasAttribute("hidden") || el.matches(OWN_ELEMENTS_SELECTOR) || el.matches(exclusion)) return;
    if (layout && el.getClientRects().length === 0) return;
    if (CONTACT_ACCOUNT_HINT.test(`${el.getAttribute("class") ?? ""} ${el.id}`)) return;
    const block = CONTACT_BLOCK_TAGS.has(el.tagName.toUpperCase());
    if (block) flush();
    el.childNodes.forEach(walk);
    if (el.tagName === "A") {
      const payload = linkPayload(el);
      if (payload && !current.replace(/\s+/g, "").includes(payload.replace(/\s+/g, ""))) current += ` ${payload}`;
    }
    if (block) flush();
  };
  walk(root);
  flush();
}

/**
 * Liniile de contact din header/footer/topbar care NU sunt deja în `kept`,
 * deduplicate și încadrate în `CONTACT_MAX_CHARS`. Zonele din interiorul lui
 * `main`/`article`, cele excluse și ale widgetului sunt ignorate.
 */
function missingContactLines(kept: string): string[] {
  const candidates = Array.from(document.querySelectorAll(CONTACT_ROOT_SELECTOR)).filter(
    (el) => !el.closest(MAIN_SELECTOR) && !el.querySelector(MAIN_SELECTOR) && !isOwnElement(el) && !isExcludedElement(el),
  );
  // zone imbricate (un <footer> într-un [role=contentinfo]): rămâne cea exterioară
  const roots = candidates.filter((el) => !candidates.some((other) => other !== el && other.contains(el)));
  const haystack = kept.toLowerCase();
  const seen = new Set<string>();
  const result: string[] = [];
  let used = 0;
  for (const root of roots.slice(0, CONTACT_MAX_ROOTS)) {
    const lines: string[] = [];
    collectContactLines(root, lines);
    for (const line of lines) {
      if (line.length > CONTACT_LINE_MAX || !looksLikeContact(line)) continue;
      const key = line.toLowerCase();
      if (seen.has(key) || haystack.includes(key)) continue;
      if (used + line.length + 1 > CONTACT_MAX_CHARS) continue;
      seen.add(key);
      result.push(line);
      used += line.length + 1;
    }
  }
  return result;
}

/**
 * Blocul de contact gata formatat pentru ce a rămas din text (`""` dacă nu
 * lipsește nimic). Folosit și de scanul din builder (`builder/dom.ts`), care taie
 * textul paginii la un plafon propriu - aceeași pierdere de header/footer.
 */
export function siteContactBlock(kept: string): string {
  const lines = missingContactLines(kept);
  return lines.length > 0 ? `${CONTACT_LABEL}${lines.join("\n")}` : "";
}

/** Cât să lase apelantul liber sub plafonul lui, ca blocul de contact să încapă. */
export const SITE_CONTACT_RESERVE = CONTACT_RESERVE;

function captureText(): string {
  const full = visibleText(document.body, [], commerceActive);
  if (full.length <= SNAPSHOT_MAX_TEXT_CHARS) return full;
  // Pagina întreagă nu încape: `main`/`article` e o selecție mai bună decât o
  // tăietură oarbă (scoate meniuri, footer, cookie banners) - măsurat în
  // docs/analiza-dom-runtime.md. Dacă situl n-are `main`, mergem pe tăietură.
  const main = document.querySelector(MAIN_SELECTOR);
  const text =
    main && !isOwnElement(main) && !isExcludedElement(main)
      ? visibleText(main as HTMLElement, [], commerceActive)
      : full;
  // Contactul din header/footer nu trebuie să plece odată cu zgomotul (C10c):
  // dacă lipsesc liniile de contact din ce rămâne, le adăugăm într-un bloc mic.
  // Fără astfel de linii, rezultatul e exact cel de dinainte.
  const body = truncateAtParagraph(text, SNAPSHOT_MAX_TEXT_CHARS - CONTACT_RESERVE);
  const block = siteContactBlock(body);
  if (!block) return truncateAtParagraph(text, SNAPSHOT_MAX_TEXT_CHARS);
  return `${body}${block}`;
}

function isInViewport(el: Element): boolean {
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;
  return rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
}

function isNavigation(el: Element): boolean {
  return el.closest("nav, header, [role='navigation'], [role='banner']") !== null;
}

/**
 * Ce se sacrifică primul când pagina are mai multe elemente decât plafonul:
 * ce e pe ecran acum (despre asta întreabă omul) → navigația (drumul spre
 * restul sitului) → restul.
 */
function priorityRank(el: Element): number {
  if (isInViewport(el)) return 0;
  if (isNavigation(el)) return 1;
  return 2;
}

/**
 * Sub plafon, elementele rămân în ordinea documentului - e ordinea în care
 * le vede și le parcurge omul, deci și cea în care are sens să le citească
 * modelul. Doar când trebuie să tăiem intervine prioritatea, iar ordinea din
 * document se păstrează în interiorul fiecărei grupe (sortare stabilă).
 */
function selectTargets(candidates: Element[]): Element[] {
  if (candidates.length <= SNAPSHOT_MAX_ELEMENTS) return candidates;
  const kept = new Set(
    candidates
      .map((el, documentOrder) => ({ el, rank: priorityRank(el), documentOrder }))
      .sort((a, b) => (a.rank !== b.rank ? a.rank - b.rank : a.documentOrder - b.documentOrder))
      .slice(0, SNAPSHOT_MAX_ELEMENTS)
      .map((c) => c.el),
  );
  return candidates.filter((el) => kept.has(el));
}

/**
 * Href-ul unui link, normalizat la un path al aplicației.
 *
 * Nu e cosmetică: href-ul ajunge în snapshot, iar modelul îl compară cu `url`-ul
 * paginilor din site index ca să aleagă ce link să evidențieze pentru o
 * pagină-țintă. Un sit care își scrie linkurile relativ - `./pricing`, ca situl
 * demo - ar produce un href care nu corespunde cu niciun `url` din index, deci
 * rutarea cross-page ar rata linkul potrivit și ar cădea pe „numește pagina".
 * Rezolvarea se face aici pentru că doar browserul știe URL-ul de bază față de
 * care „./pricing" înseamnă ceva. (Aceeași normalizare hrănea și acțiunea
 * `navigate`, retrasă 19 iul 2026.)
 *
 * Fără query, ca `currentPagePath()` (page.ts) și din același motiv: ține
 * filtre și tokenuri volatile. Linkurile externe (și mailto:/tel:) rămân
 * întregi și neatinse - sunt oneste așa, iar vizitatorul decide dacă le urmează.
 */
function normalizedHref(el: Element): string | undefined {
  const raw = el.getAttribute("href");
  if (!raw) return undefined;
  try {
    const url = new URL(raw, location.href);
    if (url.origin !== location.origin) return url.href;
    return `${url.pathname}${url.hash}`;
  } catch {
    return raw;
  }
}

export interface CapturedSnapshot {
  snapshot: PageSnapshot;
  /** idx → nodul viu; nu pleacă niciodată la server. */
  map: Map<number, Element>;
}

export function capturePageSnapshot(): CapturedSnapshot {
  const targetable = [...document.querySelectorAll(TARGETABLE_SELECTOR)].filter(
    (el) => !isOwnElement(el) && !isExcludedElement(el),
  );
  // Vizibilitatea se citește o singură dată aici, într-o singură trecere peste
  // toți candidații - browserul face un singur style/layout flush, nu unul per
  // element intercalat cu celelalte citiri de mai jos (etichete, href-uri).
  const candidates = hasLayoutEngine()
    ? targetable.filter((el) => isVisible(el) && !isCovered(el))
    : targetable;

  const map = new Map<number, Element>();
  const elements = selectTargets(candidates).map((el, idx) => {
    map.set(idx, el);
    const text = snapshotLabelFor(el).slice(0, SNAPSHOT_MAX_ELEMENT_TEXT_CHARS);
    const href = el.tagName === "A" ? normalizedHref(el) : undefined;
    const state = disclosureStateOf(el);
    return {
      idx,
      tag: el.tagName.toLowerCase(),
      text,
      ...(href ? { href: href.slice(0, SNAPSHOT_MAX_HREF_CHARS) } : {}),
      ...(state ? { state } : {}),
    };
  });

  return { snapshot: { text: captureText(), elements }, map };
}
